import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Shield, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { authService } from '@/shared/lib/auth';
import { useAuth } from '@/shared/hooks/useAuth';
import { MfaApiService, type MfaSetupResponse } from '@/features/mfa/api/mfaApi';
import { MfaQrDisplay } from '@/features/mfa/components/MfaQrDisplay';
import { MfaCodeInput } from '@/features/mfa/components/MfaCodeInput';
import { MfaRecoveryCodesView } from '@/features/mfa/components/MfaRecoveryCodesView';

type Step = 'loading' | 'qr' | 'recovery' | 'error';

const MfaSetupPage: React.FC = () => {
  const { t } = useTranslation('mfa');
  // tRef: o efeito abaixo dispara startSetupChallenge por challenge/navigate — `t`
  // não entra nas deps (manual §2.10) senão a troca de idioma reiniciaria o desafio.
  const tRef = useRef(t);
  tRef.current = t;
  const navigate = useNavigate();
  const { finalizeAfterMfa } = useAuth();

  const challenge = useMemo(() => authService.getMfaChallenge(), []);
  const [step, setStep] = useState<Step>('loading');
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  useEffect(() => {
    if (!challenge) {
      navigate('/login', { replace: true });
      return;
    }
    if (!challenge.requiresSetup) {
      navigate('/login/mfa', { replace: true });
      return;
    }
    let cancelled = false;
    MfaApiService.startSetupChallenge(challenge.mfaSessionToken)
      .then((s) => {
        if (cancelled) return;
        setSetup(s);
        setStep('qr');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractMessage(err) ?? tRef.current('setupPage.startErrorFallback'));
        setStep('error');
      });
    return () => {
      cancelled = true;
    };
  }, [challenge, navigate]);

  const handleConfirm = async () => {
    if (!challenge || !setup || code.length !== 6 || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await MfaApiService.confirmSetupChallenge(
        challenge.mfaSessionToken,
        setup.secret,
        code
      );
      if (result.auth) {
        authService.completeLogin(result.auth);
        finalizeAfterMfa(result.auth.user);
      }
      setRecoveryCodes(result.recoveryCodes);
      setStep('recovery');
    } catch (err) {
      setError(extractMessage(err) ?? t('setupPage.invalidCodeRetry'));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1f2e] flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl p-8 border border-white/10 bg-[#20263a]/95">
        <div className="flex flex-col items-center mb-6 text-center">
          {step === 'recovery' ? (
            <ShieldCheck className="w-12 h-12 text-emerald-400 mb-3" />
          ) : (
            <Shield className="w-12 h-12 text-cyan-400 mb-3" />
          )}
          <h1 className="text-xl font-semibold text-white">
            {step === 'recovery' ? t('setupPage.configuredTitle') : t('setupPage.configureTitle')}
          </h1>
          <p className="text-sm text-gray-400 mt-1 max-w-md">
            {step === 'qr' && t('setupPage.qrDescription')}
            {step === 'recovery' && t('setupPage.recoveryDescription')}
            {step === 'loading' && t('setupPage.loadingDescription')}
            {step === 'error' && t('setupPage.errorDescription')}
          </p>
        </div>

        {error && step !== 'error' && (
          <Alert variant="destructive" className="mb-4 bg-red-500/10 border-red-500/30 text-red-400">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === 'loading' && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-400" />
          </div>
        )}

        {step === 'error' && (
          <div className="space-y-4">
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button
              className="w-full"
              onClick={() => {
                authService.clearMfaChallenge();
                navigate('/login', { replace: true });
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('setupPage.backToLoginButton')}
            </Button>
          </div>
        )}

        {step === 'qr' && setup && (
          <div className="space-y-6">
            <ol className="space-y-2 text-sm text-gray-300">
              <li>{t('setupPage.step1')}</li>
              <li>{t('setupPage.step2')}</li>
              <li>{t('setupPage.step3')}</li>
            </ol>
            <MfaQrDisplay otpAuthUri={setup.otpAuthUri} secret={setup.secret} />
            <div className="flex flex-col items-center space-y-3">
              <p className="text-sm text-gray-300">{t('setupPage.appCodeLabel')}</p>
              <MfaCodeInput
                value={code}
                onChange={setCode}
                onComplete={() => handleConfirm()}
                disabled={submitting}
                autoFocus
              />
              <Button
                className="w-full"
                onClick={handleConfirm}
                disabled={code.length !== 6 || submitting}
              >
                {submitting ? t('setupPage.validating') : t('setupPage.confirmButton')}
              </Button>
            </div>
          </div>
        )}

        {step === 'recovery' && (
          <MfaRecoveryCodesView
            codes={recoveryCodes}
            onConfirmSaved={() => navigate('/dashboard', { replace: true })}
            confirmLabel={t('setupPage.continueButton')}
          />
        )}
      </div>
    </div>
  );
};

function extractMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const e = err as Record<string, any>;
  return (
    e?.response?.data?.message ??
    e?.error?.message ??
    e?.message ??
    null
  );
}

export default MfaSetupPage;
